// src/services/inviteService.js
//
// Phase 5 (PLAN1.md §H.1) — Bulk teacher/parent invites.
// -----------------------------------------------------------------------
// An invite is a placeholder `users/{pendingUid}` document. When the
// invited person later completes signup with the same email, a Cloud
// Function reconciles their real Auth uid with the pending doc (merging
// the tenantId + role onto the real profile and deleting the placeholder).
//
// Until that Cloud Function ships, the placeholder is still useful: the
// admin panel lists it in the user roster so a new tenant can SEE the
// state of their invites, and an admin can send the invite link out-of-
// band (email, WhatsApp). The shape of the doc matches the existing
// TenantAdmin.handleInvite convention so both surfaces stay interoperable.
// -----------------------------------------------------------------------

import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { ROLES } from './userService';
import { inviteService as inviteServiceV2 } from './inviteServiceV2';

const AUTH_BACKEND = process.env.REACT_APP_AUTH_BACKEND || 'firebase';

const USERS_COLLECTION = 'users';

// Basic RFC-5322-ish email check; intentionally forgiving — we'd rather
// send a bad invite than silently drop one the admin typed in haste.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (value) =>
  typeof value === 'string' && EMAIL_RE.test(value.trim());

/**
 * Normalise and de-duplicate a free-form list of emails. Accepts the raw
 * textarea string (comma / semicolon / newline separated). Returns:
 *   { valid: string[], invalid: string[] }
 * so the UI can show which entries were rejected without throwing.
 */
export const parseEmailList = (raw) => {
  if (typeof raw !== 'string' || !raw.trim()) return { valid: [], invalid: [] };
  const seen = new Set();
  const valid = [];
  const invalid = [];
  const tokens = raw
    .split(/[,;\n\r\t]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  for (const tok of tokens) {
    const lower = tok.toLowerCase();
    if (seen.has(lower)) continue;
    seen.add(lower);
    if (isValidEmail(tok)) valid.push(lower);
    else invalid.push(tok);
  }
  return { valid, invalid };
};

/** Stable-ish pending uid — timestamp + random suffix to avoid collisions. */
const buildPendingUid = () =>
  `invite_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const inviteServiceFirebase = {
  /**
   * Create a single invite placeholder. Idempotent by email within a
   * tenant: if an invite for that email already exists we return the
   * existing doc rather than creating a duplicate.
   */
  async invite({ tenantId, email, role = ROLES.TEACHER, invitedBy = null }) {
    if (!tenantId) throw new Error('inviteService: tenantId is required');
    if (!isValidEmail(email)) throw new Error('inviteService: invalid email');
    const normalised = email.trim().toLowerCase();

    // Check for an existing invite (same tenant, same email, pending).
    const existing = await getDocs(
      query(
        collection(db, USERS_COLLECTION),
        where('tenantId', '==', tenantId),
        where('email', '==', normalised),
        where('invited', '==', true),
      ),
    );
    if (!existing.empty) {
      const first = existing.docs[0];
      return { id: first.id, ...first.data(), alreadyExisted: true };
    }

    const uid = buildPendingUid();
    const payload = {
      email: normalised,
      role,
      tenantId,
      invited: true,
      invitedBy: invitedBy || null,
      invitedAt: Timestamp.now(),
    };
    await setDoc(doc(db, USERS_COLLECTION, uid), payload);
    return { id: uid, ...payload, alreadyExisted: false };
  },

  /**
   * Bulk invite — invites each email in sequence (Firestore doesn't let
   * us batch the dedup-check read and the write atomically anyway).
   * Never throws: per-email failures surface in the returned breakdown
   * so the onboarding wizard can tell the admin exactly who was reached.
   */
  async inviteMany({ tenantId, emails, role = ROLES.TEACHER, invitedBy = null }) {
    const results = { created: [], duplicates: [], failed: [] };
    if (!tenantId || !Array.isArray(emails) || emails.length === 0) {
      return results;
    }
    for (const email of emails) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const r = await inviteServiceFirebase.invite({ tenantId, email, role, invitedBy });
        if (r.alreadyExisted) results.duplicates.push(r);
        else results.created.push(r);
      } catch (err) {
        results.failed.push({ email, message: err?.message || 'unknown error' });
      }
    }
    return results;
  },

  /** List pending invites for a tenant (for the admin roster / wizard review). */
  async listPending(tenantId) {
    if (!tenantId) return [];
    const snap = await getDocs(
      query(
        collection(db, USERS_COLLECTION),
        where('tenantId', '==', tenantId),
        where('invited', '==', true),
      ),
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },
};

// Phase 2.12 selector — switches at module load.
export const inviteService = AUTH_BACKEND === 'api' ? inviteServiceV2 : inviteServiceFirebase;

export default inviteService;
